import { Controller } from "@hotwired/stimulus"

const STEP_DIG_START = 1
const STEP_DIG_WAIT_REWARD = 2
const STEP_ACHIEVEMENTS_NAV = 3
const STEP_ACHIEVEMENTS_UNCLAIMED = 4
const STEP_RESOURCES = 5
const STEP_UPGRADES = 6
const STEP_POST_UPGRADES_PROCESS_NAV = 7
const STEP_POST_UPGRADES_DIG_1 = 8
const STEP_POST_UPGRADES_BADGE = 9
const STEP_POST_UPGRADES_DIG_2 = 10
const STEP_POST_UPGRADES_QUEUE_MSG = 11
const STEP_POST_UPGRADES_DIG_3 = 12
const STEP_POST_UPGRADES_OFFLINE_MSG = 13
const STEP_PROFILE_LINK = 14
const STEP_PROFILE = 15
const STEP_SETTINGS = 16
const STEP_IDENTITY = 17
const STEP_ONLINE_OPERATORS = 18
const STEP_COMM_INTRO = 19
const STEP_PERFORMANCE_NAV = 20
const STEP_PERFORMANCE_PAGE = 21
const COMPLETION_STEP = 22

const TOOLTIP_DEFS = Object.freeze({
  dig_start: { text: "Click ▓︎ to begin processing!", variant: "warning", position: "top" },
  dig_wait_reward: { text: "Well done! Now wait for process completion, it can take a while at first...", variant: "success", position: "top" },
  achievements_nav: { text: "Claim your achievements to get rewards!", variant: "info", position: "top" },
  achievements_unclaimed: { text: "Claim your available achievements", variant: "info", position: "top" },
  resources_deepcoins: { text: "You are rich now! Lets spend some DC", variant: "info", position: "top" },
  upgrades_choose: { text: "Choose all available upgrades", variant: "info", position: "top" },
  upgrades_stats: { text: "Upgrades increase your stats", variant: "info", position: "bottom" },
  profile_link: { text: "Go to your profile", variant: "info", position: "bottom" },
  profile_intro: { text: "This is your profile with all the relevant stats.", variant: "info", position: "top" },
  profile_to_settings: { text: "To proceed, go here", variant: "info", position: "top" },
  process_nav_after_upgrades: { text: "Lets see how you do now", variant: "info", position: "top" },
  dig_more: { text: "Process some more blocks", variant: "warning", position: "top" },
  tiles_defeated_badge: {
    text: "Processing blocks make you progress, when you got 25 you can promote from Intern",
    variant: "info",
    position: "top"
  },
  unlock_queue_after_25: {
    text: "Processing blocks become more rewarding over time and you can unlock a queue system for longer processing times!",
    variant: "info",
    position: "top"
  },
  offline_processing: { text: "Your process and queue happens even with browser closed", variant: "info", position: "top" },
  settings_intro: {
    text: "This is your settings, here you can customize color, theme and more",
    variant: "info",
    position: "top"
  },
  settings_to_identity: { text: "To proceed, go here", variant: "info", position: "top" },
  identity_update: {
    text: "Change operator handle and password to secure your account and unlock achievements",
    variant: "info",
    position: "top"
  },
  online_operators: { text: "See who else is online here", variant: "info", position: "top" },
  comm_intro: { text: "Talk to online operators here", variant: "info", position: "left" },
  performance_nav: { text: "Compare yourself against others in the leaderboards", variant: "info", position: "top" },
  performance_done: {
    text: "Once promoted, you will appear here. This concludes the guided tutorial, but you can continue to explore the game and get more rewards!",
    variant: "info",
    position: "bottom"
  }
})

const TOTAL_STEPS = COMPLETION_STEP - 1

const STEP_SUMMARIES = Object.freeze({
  [STEP_DIG_START]: "Start processing a block in the grid.",
  [STEP_DIG_WAIT_REWARD]: "Wait for the first process to finish so you earn DeepCoins.",
  [STEP_ACHIEVEMENTS_NAV]: "Open Achievements to claim your rewards.",
  [STEP_ACHIEVEMENTS_UNCLAIMED]: "Claim any available achievements.",
  [STEP_RESOURCES]: "Open Resources to spend your DeepCoins.",
  [STEP_UPGRADES]: "Choose upgrades and review how they change your stats.",
  [STEP_POST_UPGRADES_PROCESS_NAV]: "Return to Processing to use your new upgrades.",
  [STEP_POST_UPGRADES_DIG_1]: "Process a few more blocks.",
  [STEP_POST_UPGRADES_BADGE]: "Check the tiles defeated badge and keep processing.",
  [STEP_POST_UPGRADES_DIG_2]: "Process a few more blocks.",
  [STEP_POST_UPGRADES_QUEUE_MSG]: "Read the process queue unlock message.",
  [STEP_POST_UPGRADES_DIG_3]: "Process a few more blocks.",
  [STEP_POST_UPGRADES_OFFLINE_MSG]: "Learn about offline processing.",
  [STEP_PROFILE_LINK]: "Open your operator profile.",
  [STEP_PROFILE]: "Review your profile stats, then go to Settings.",
  [STEP_SETTINGS]: "Review Settings, then open Identity.",
  [STEP_IDENTITY]: "Update your operator handle and password.",
  [STEP_ONLINE_OPERATORS]: "View the online operators list.",
  [STEP_COMM_INTRO]: "Open the comms panel to chat.",
  [STEP_PERFORMANCE_NAV]: "Open the performance leaderboards.",
  [STEP_PERFORMANCE_PAGE]: "Review the leaderboards to wrap up the tutorial."
})

export default class extends Controller {
  static identifier = "tutorial"

  static targets = [
    "gridCard",
    "gridAside",
    "achievementsLink",
    "sidebarToggle",
    "sidebarProcessLink",
    "unclaimedAchievements",
    "sidebarDeepcoinsLink",
    "sidebarRecursiveCoinsLink",
    "commArea",
    "sidebarRecursion",
    "sidebarAchievements",
    "sidebarPerformance",
    "performanceLink",
    "sidebarShop",
    "sidebarOnlineOperators",
    "onlineOperatorsLink",
    "sidebarMsg",
    "sidebarResourcesDivider",
    "sidebarResourcesContent",
    "sidebarNameText",
    "sidebarNameLink",
    "sidebarDrawer",
    "sidebarStatsDivider",
    "sidebarStatsContent",
    "tilesDefeatedBadge",
    "upgradesRecursiveTab",
    "upgradesSpecializationTab",
    "upgradesDeepcoinPanel",
    "workerProfileSection",
    "profileSettingsTab",
    "profileIdentityTab",
    "profileSettingsPanel",
    "profileSettingsTopFieldset",
    "profileOperatorProfileFieldset",
    "profileIdentityFieldset",
    "profileIdentityForm",
    "profileUpdateIdentityButton",
    "performancePageHeader",
    "tutorialStepCounter",
    "tutorialStepDescription",
    "tutorialStepActions",
    "tutorialStepSkip",
    "tutorialStepEnd",
    "tutorialStepRestart",
    "tutorialStepInactive",
    "tutorialStepCompleted"
  ]

  static values = {
    workerId: Number,
    tutorialMode: Boolean,
    timesDug: Number,
    deepCoins: Number,
    tutorialModePath: String
  }

  connect() {
    if (!this.ensureInitialized()) return

    this.registerEventListeners()

    this.reconcileFromSnapshot()
    this.render()
  }

  registerEventListeners() {
    if (this.listenersRegistered) return

    this.onGameUpdate = this.onGameUpdate.bind(this)
    this.onTurboLoad = this.onTurboLoad.bind(this)
    this.onTurboFrameLoad = this.onTurboFrameLoad.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onDocumentChange = this.onDocumentChange.bind(this)
    this.onDocumentSubmit = this.onDocumentSubmit.bind(this)

    window.addEventListener("deepco:game_update", this.onGameUpdate)
    document.addEventListener("turbo:load", this.onTurboLoad)
    document.addEventListener("turbo:frame-load", this.onTurboFrameLoad)
    document.addEventListener("click", this.onDocumentClick, true)
    document.addEventListener("change", this.onDocumentChange, true)
    document.addEventListener("submit", this.onDocumentSubmit, true)

    this.listenersRegistered = true
  }

  disconnect() {
    if (this.listenersRegistered) {
      window.removeEventListener("deepco:game_update", this.onGameUpdate)
      document.removeEventListener("turbo:load", this.onTurboLoad)
      document.removeEventListener("turbo:frame-load", this.onTurboFrameLoad)
      document.removeEventListener("click", this.onDocumentClick, true)
      document.removeEventListener("change", this.onDocumentChange, true)
      document.removeEventListener("submit", this.onDocumentSubmit, true)
      this.listenersRegistered = false
    }
    if (this.sidebarRenderTimeout) {
      window.clearTimeout(this.sidebarRenderTimeout)
      this.sidebarRenderTimeout = null
    }
  }

  onTurboLoad() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  onTurboFrameLoad(event) {
    if (!this.ensureInitialized()) return
    if (
      event?.target?.id === "grid-panel" ||
      event?.target?.id === "achievements-list" ||
      event?.target?.id === "deepcoin-upgrades" ||
      event?.target?.id === "tiles-defeated-badge" ||
      event?.target?.id === "online_count_frame" ||
      event?.target?.id === "main-panel" ||
      String(event?.target?.id || "").startsWith("damage-stats-")
    ) {
      this.reconcileFromSnapshot()
      this.render()
    }
  }

  gridCardTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  gridCardTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  gridAsideTargetConnected() {
    if (!this.ensureInitialized()) return
    this.render()
  }

  gridAsideTargetDisconnected(element) {
    this.showGridAside(element)
  }

  achievementsLinkTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  achievementsLinkTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  sidebarToggleTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  sidebarToggleTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  sidebarProcessLinkTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  sidebarProcessLinkTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  onlineOperatorsLinkTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  onlineOperatorsLinkTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  unclaimedAchievementsTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  unclaimedAchievementsTargetDisconnected(element) {
    this.clearTooltipFrom(element)
    this.reconcileFromSnapshot()
    this.render()
  }

  sidebarDeepcoinsLinkTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  sidebarDeepcoinsLinkTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  sidebarRecursiveCoinsLinkTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applySidebarRecursiveCoinsVisibility()
  }

  sidebarRecursiveCoinsLinkTargetDisconnected(element) {
    this.showElement(element)
  }

  commAreaTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyRestrictedUiVisibility()
  }

  commAreaTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarRecursionTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyRestrictedUiVisibility()
  }

  sidebarRecursionTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarAchievementsTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyAchievementsVisibility()
  }

  sidebarAchievementsTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarPerformanceTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyRestrictedUiVisibility()
  }

  sidebarPerformanceTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarShopTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyRestrictedUiVisibility()
  }

  sidebarShopTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarOnlineOperatorsTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyRestrictedUiVisibility()
  }

  sidebarOnlineOperatorsTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarMsgTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyRestrictedUiVisibility()
  }

  sidebarMsgTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarResourcesDividerTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applySidebarResourcesVisibility()
  }

  sidebarResourcesDividerTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarResourcesContentTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applySidebarResourcesVisibility()
  }

  sidebarResourcesContentTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarNameTextTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applySidebarProfileVisibility()
  }

  sidebarNameTextTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarNameLinkTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applySidebarProfileVisibility()
  }

  sidebarNameLinkTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarDrawerTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applySidebarDrawerVisibility()
  }

  sidebarDrawerTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarStatsDividerTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  sidebarStatsDividerTargetDisconnected(element) {
    this.showElement(element)
  }

  sidebarStatsContentTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  sidebarStatsContentTargetDisconnected(element) {
    this.showElement(element)
  }

  tilesDefeatedBadgeTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  tilesDefeatedBadgeTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  workerProfileSectionTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  workerProfileSectionTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileSettingsTabTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileSettingsTabTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileIdentityTabTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileIdentityTabTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileSettingsPanelTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileSettingsPanelTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileSettingsTopFieldsetTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileSettingsTopFieldsetTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileOperatorProfileFieldsetTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileOperatorProfileFieldsetTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileIdentityFormTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileIdentityFormTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileIdentityFieldsetTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileIdentityFieldsetTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  profileUpdateIdentityButtonTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  profileUpdateIdentityButtonTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  upgradesRecursiveTabTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyUpgradesTabsVisibility()
  }

  upgradesRecursiveTabTargetDisconnected(element) {
    this.showElement(element)
  }

  upgradesSpecializationTabTargetConnected() {
    if (!this.ensureInitialized()) return
    this.applyUpgradesTabsVisibility()
  }

  upgradesSpecializationTabTargetDisconnected(element) {
    this.showElement(element)
  }

  upgradesDeepcoinPanelTargetConnected() {
    if (!this.ensureInitialized()) return
    this.reconcileFromSnapshot()
    this.render()
  }

  upgradesDeepcoinPanelTargetDisconnected(element) {
    this.clearTooltipFrom(element)
  }

  onGameUpdate(event) {
    if (!this.ensureInitialized()) return
    const payload = event?.detail
    if (!payload || typeof payload !== "object") return

    if (payload.type === "damage_batch") {
      this.handleDamageBatch(payload)
      return
    }

    if (payload.type === "worker_state") {
      this.handleWorkerState(payload)
    }
  }

  onDocumentClick(event) {
    if (!this.ensureInitialized()) return
    if (this.state.completed) return

    const target = event?.target
    if (!(target instanceof Element)) return

    if (this.state.step === STEP_PERFORMANCE_PAGE) {
      void this.finishTutorial({ render: true })
      return
    }

    if (
      [
        STEP_DIG_START,
        STEP_POST_UPGRADES_DIG_1,
        STEP_POST_UPGRADES_DIG_2,
        STEP_POST_UPGRADES_DIG_3
      ].includes(this.state.step) &&
      this.isOnDigPage()
    ) {
      const tileWrapper = target.closest('.dig-tile-wrapper[data-controller~="tile"]')
      const isStartMiningWrapper =
        tileWrapper && typeof tileWrapper.getAttribute("data-action") === "string"
          ? tileWrapper.getAttribute("data-action").includes("tile#startMining")
          : Boolean(tileWrapper)

      if (isStartMiningWrapper) {
        const tileId = this.tileIdFromWrapper(tileWrapper)
        this.syncTilesDefeatedFromDom({ save: false })

        if (this.state.step === STEP_DIG_START) {
          this.completeStep1({ render: true })
          return
        }

        if (this.state.step === STEP_POST_UPGRADES_DIG_1) {
          this.state.gridAsideUnlocked = true
          this.state.awaitedDigTileId = tileId
          this.state.awaitedTilesDefeated = this.state.tilesDefeated
          this.advanceToStep(STEP_POST_UPGRADES_BADGE, { render: true })
          return
        }

        if (this.state.step === STEP_POST_UPGRADES_DIG_2) {
          this.state.awaitedDigTileId = tileId
          this.state.awaitedTilesDefeated = this.state.tilesDefeated
          this.advanceToStep(STEP_POST_UPGRADES_QUEUE_MSG, { render: true })
          return
        }

        if (this.state.step === STEP_POST_UPGRADES_DIG_3) {
          this.state.awaitedDigTileId = tileId
          this.state.awaitedTilesDefeated = this.state.tilesDefeated
          this.advanceToStep(STEP_POST_UPGRADES_OFFLINE_MSG, { render: true })
          return
        }
        return
      }
    }

    if (this.state.step === STEP_COMM_INTRO) {
      const commTrigger = target.closest('button[aria-controls="utility-panel-comm"]')
      if (commTrigger) {
        this.advanceToStep(STEP_PERFORMANCE_NAV, { render: true })
        return
      }
    }

    if (this.state.step !== STEP_UPGRADES) return
    if (!this.isOnUpgradesPage()) return
    if (!this.hasUpgradesDeepcoinPanelTarget) return

    const actionable = target.closest("button, a, form")
    if (!actionable) return
    if (!this.upgradesDeepcoinPanelTarget.contains(actionable)) return

    if (this.state.upgradesInteracted) return
    this.state.upgradesInteracted = true
    this.state.updatedAt = Date.now()
    this.saveState()
    this.render()
  }

  onDocumentChange(event) {
    if (!this.ensureInitialized()) return
    if (this.state.completed) return
    this.reconcileFromSnapshot()
    this.render()
    if (event?.target?.id === "deepco-sidebar-toggle") {
      this.scheduleSidebarRender()
    }
  }

  onDocumentSubmit(event) {
    if (!this.ensureInitialized()) return
    if (this.state.completed) return
    if (this.state.step !== STEP_IDENTITY) return
    if (!this.isOnMyProfilePage()) return
    if (!this.isWorkerTabActive("identity")) return
    if (!this.hasProfileIdentityFormTarget) return

    const form = event?.target
    if (!(form instanceof HTMLFormElement)) return
    if (form !== this.profileIdentityFormTarget) return

    this.state.identityUpdateSubmitted = true
    this.state.updatedAt = Date.now()
    this.saveState()
  }

  scheduleSidebarRender() {
    if (this.sidebarRenderTimeout) {
      window.clearTimeout(this.sidebarRenderTimeout)
    }
    this.sidebarRenderTimeout = window.setTimeout(() => {
      this.sidebarRenderTimeout = null
      if (!this.ensureInitialized()) return
      this.render()
    }, 200)
  }

  handleDamageBatch(payload) {
    if (this.state.completed) return
    if (
      ![
        STEP_DIG_START,
        STEP_POST_UPGRADES_BADGE,
        STEP_POST_UPGRADES_QUEUE_MSG,
        STEP_POST_UPGRADES_OFFLINE_MSG
      ].includes(this.state.step)
    ) {
      return
    }

    if (this.state.step === STEP_DIG_START) {
      const damages = Array.isArray(payload.damages) ? payload.damages : []
      const didWeDamage = damages.some((d) => Number(d?.worker_id) === this.workerIdValue)
      if (!didWeDamage) return
      this.completeStep1()
      return
    }

    if (!this.didAwaitedDigComplete(payload)) return

    if (this.state.step === STEP_POST_UPGRADES_BADGE) {
      this.advanceToStep(STEP_POST_UPGRADES_DIG_2, { render: true })
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_QUEUE_MSG) {
      this.advanceToStep(STEP_POST_UPGRADES_DIG_3, { render: true })
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_OFFLINE_MSG) {
      this.advanceToStep(STEP_PROFILE_LINK, { render: true })
    }
  }

  handleWorkerState(payload) {
    if (this.state.completed) return

    const canUpgrade = payload?.coins?.can_upgrade
    if (typeof canUpgrade === "boolean" && this.state.canUpgrade !== canUpgrade) {
      this.state.canUpgrade = canUpgrade
      this.state.updatedAt = Date.now()
      this.saveState()
    }

    if (this.state.step === STEP_DIG_WAIT_REWARD) {
      if (!this.isOnDigPage()) return
      const amount = payload?.flash_message?.amount
      if (typeof amount === "number" && amount > 0) {
        this.completeStep2()
      }
      return
    }

    if (this.state.step === STEP_UPGRADES && this.state.upgradesInteracted && this.state.canUpgrade === false) {
      this.advanceToStep(STEP_POST_UPGRADES_PROCESS_NAV, { render: true })
    }
  }

  reconcileFromSnapshot() {
    if (!this.ensureInitialized()) return
    if (this.state.completed) return

    const timesDug = this.currentTimesDug()
    const deepCoins = this.currentDeepCoins()
    const timesDugDelta = this.deltaFromRestart(timesDug, this.state.restartTimesDug)
    const deepCoinsDelta = this.deltaFromRestart(deepCoins, this.state.restartDeepCoins)

    this.syncTilesDefeatedFromDom({ save: false })

    if (this.state.step >= STEP_POST_UPGRADES_BADGE && !this.state.gridAsideUnlocked) {
      this.state.gridAsideUnlocked = true
      this.state.updatedAt = Date.now()
      this.saveState()
    }

    if (this.state.step === STEP_DIG_START && Number.isFinite(timesDugDelta) && timesDugDelta > 0) {
      this.completeStep1({ render: false })
    }

    if (this.state.step === STEP_DIG_WAIT_REWARD && Number.isFinite(deepCoinsDelta) && deepCoinsDelta > 0) {
      this.completeStep2({ render: false })
    }

    if (this.state.step === STEP_ACHIEVEMENTS_NAV && this.isOnAchievementsPage()) {
      this.advanceToStep(STEP_ACHIEVEMENTS_UNCLAIMED, { render: false })
    }

    if (this.state.step === STEP_ACHIEVEMENTS_UNCLAIMED && this.isOnAchievementsPage() && !this.hasUnclaimedAchievements()) {
      this.advanceToStep(STEP_RESOURCES, { render: false })
    }

    if (this.state.step === STEP_RESOURCES && this.isOnUpgradesPage()) {
      this.advanceToStep(STEP_UPGRADES, { render: false })
    }

    if (this.state.step === STEP_UPGRADES) {
      this.syncCanUpgradeFromDom({ save: false })
      if (this.state.upgradesInteracted && this.state.canUpgrade === false) {
        this.advanceToStep(STEP_POST_UPGRADES_PROCESS_NAV, { render: false })
      }
    }

    if (this.state.step === STEP_POST_UPGRADES_PROCESS_NAV && this.isOnDigPage()) {
      this.advanceToStep(STEP_POST_UPGRADES_DIG_1, { render: false })
    }

    this.maybeCompleteTileDefeatWaitStep({ render: false })

    if (this.state.step === STEP_PROFILE_LINK && this.isOnMyProfilePage()) {
      this.advanceToStep(STEP_PROFILE, { render: false })
    }

    if (this.state.step === STEP_PROFILE && this.isOnMyProfilePage() && this.isWorkerTabActive("settings")) {
      this.advanceToStep(STEP_SETTINGS, { render: false })
    }

    if (this.state.step === STEP_SETTINGS && this.isOnMyProfilePage() && this.isWorkerTabActive("identity")) {
      this.advanceToStep(STEP_IDENTITY, { render: false })
    }

    if (this.state.step === STEP_IDENTITY && this.state.identityUpdateSubmitted && this.hasFlashMessage("Settings updated!")) {
      this.advanceToStep(STEP_ONLINE_OPERATORS, { render: false })
    }

    if (this.state.step === STEP_ONLINE_OPERATORS && this.isOnOnlineOperatorsPage()) {
      this.advanceToStep(STEP_COMM_INTRO, { render: false })
    }

    if (this.state.step === STEP_PERFORMANCE_NAV && this.isOnPerformancePage()) {
      this.advanceToStep(STEP_PERFORMANCE_PAGE, { render: false })
    }

    if (this.state.step === STEP_IDENTITY && this.state.identityUpdateSubmitted && this.isOnMyProfilePage() && this.identityFormHasErrors()) {
      this.state.identityUpdateSubmitted = false
      this.state.updatedAt = Date.now()
      this.saveState()
    }
  }

  completeStep1({ render = true } = {}) {
    this.advanceToStep(STEP_DIG_WAIT_REWARD, { render })
  }

  completeStep2({ render = true } = {}) {
    this.advanceToStep(STEP_ACHIEVEMENTS_NAV, { render })
  }

  advanceToStep(step, { render = true } = {}) {
    this.state.step = step

    if (!this.isTileDefeatWaitStep(step)) {
      this.state.awaitedDigTileId = null
      this.state.awaitedTilesDefeated = null
    }

    this.state.updatedAt = Date.now()
    this.saveState()
    this.updateTutorialPanel()
    if (render) this.render()
  }

  async finishTutorial({ render = true } = {}) {
    if (this.state.completed) return

    this.state.step = COMPLETION_STEP
    this.state.completed = true
    this.state.updatedAt = Date.now()
    this.saveState()
    this.updateTutorialPanel()

    if (this.hasTutorialModePathValue && this.tutorialModePathValue) {
      await this.persistTutorialMode(false)
    }

    if (render) this.render()
  }

  async persistTutorialMode(value) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const body = JSON.stringify({ tutorial_mode: value })

    try {
      const response = await fetch(this.tutorialModePathValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {})
        },
        credentials: "same-origin",
        body
      })

      if (!response.ok) {
        console.warn("[tutorial] Failed to persist tutorial_mode:", response.status)
      }
    } catch (error) {
      console.warn("[tutorial] Failed to persist tutorial_mode:", error)
    }
  }

  skipStep(event) {
    event?.preventDefault()
    if (!this.ensureInitialized()) return
    if (this.state.completed) return

    const currentStep = Number.isFinite(this.state.step) ? this.state.step : STEP_DIG_START
    if (currentStep >= COMPLETION_STEP - 1) {
      void this.finishTutorial({ render: true })
      return
    }

    this.advanceToStep(currentStep + 1, { render: true })
  }

  endTutorial(event) {
    event?.preventDefault()
    if (!this.ensureInitialized()) return
    void this.finishTutorial({ render: true })
  }

  restartTutorial(event) {
    event?.preventDefault()
    if (!this.hasWorkerIdValue || !Number.isFinite(this.workerIdValue)) return
    if (this.hasTutorialModeValue) this.tutorialModeValue = true

    if (!this.initialized) {
      this.storageKey = `deepco:tutorial:v1:worker:${this.workerIdValue}`
      this.initialized = true
    }

    const nextState = this.initialState()
    nextState.restartTimesDug = this.currentTimesDug()
    nextState.restartDeepCoins = this.currentDeepCoins()
    this.state = nextState
    this.saveState()
    this.updateTutorialPanel()
    
    // Register event listeners if they haven't been registered yet
    // This is necessary when restarting from tutorial_mode: false
    this.registerEventListeners()
    
    this.render()

    if (this.hasTutorialModePathValue && this.tutorialModePathValue) {
      void this.persistTutorialMode(true)
    }
  }

  updateTutorialPanel() {
    if (
      !this.hasTutorialStepCounterTarget &&
      !this.hasTutorialStepDescriptionTarget &&
      !this.hasTutorialStepActionsTarget &&
      !this.hasTutorialStepSkipTarget &&
      !this.hasTutorialStepEndTarget &&
      !this.hasTutorialStepRestartTarget &&
      !this.hasTutorialStepInactiveTarget &&
      !this.hasTutorialStepCompletedTarget
    ) {
      return
    }

    if (this.hasTutorialStepInactiveTarget) {
      this.tutorialStepInactiveTarget.classList.add("hidden")
    }

    if (this.state.completed || this.state.step >= COMPLETION_STEP) {
      if (this.hasTutorialStepCounterTarget) this.tutorialStepCounterTarget.classList.add("hidden")
      if (this.hasTutorialStepDescriptionTarget) this.tutorialStepDescriptionTarget.classList.add("hidden")
      if (this.hasTutorialStepActionsTarget) this.tutorialStepActionsTarget.classList.remove("hidden")
      if (this.hasTutorialStepSkipTarget) this.tutorialStepSkipTarget.classList.add("hidden")
      if (this.hasTutorialStepEndTarget) this.tutorialStepEndTarget.classList.add("hidden")
      if (this.hasTutorialStepRestartTarget) this.tutorialStepRestartTarget.classList.remove("hidden")
      if (this.hasTutorialStepCompletedTarget) this.tutorialStepCompletedTarget.classList.remove("hidden")
      return
    }

    const rawStep = Number.isFinite(this.state.step) ? this.state.step : STEP_DIG_START
    const step = Math.min(Math.max(rawStep, 1), TOTAL_STEPS)
    const summary = STEP_SUMMARIES[step] || "Follow the highlighted prompt to continue."

    if (this.hasTutorialStepCounterTarget) {
      this.tutorialStepCounterTarget.textContent = `Step ${step} of ${TOTAL_STEPS}`
      this.tutorialStepCounterTarget.classList.remove("hidden")
    }

    if (this.hasTutorialStepDescriptionTarget) {
      this.tutorialStepDescriptionTarget.textContent = summary
      this.tutorialStepDescriptionTarget.classList.remove("hidden")
    }

    if (this.hasTutorialStepActionsTarget) this.tutorialStepActionsTarget.classList.remove("hidden")
    if (this.hasTutorialStepSkipTarget) this.tutorialStepSkipTarget.classList.remove("hidden")
    if (this.hasTutorialStepEndTarget) this.tutorialStepEndTarget.classList.remove("hidden")
    if (this.hasTutorialStepRestartTarget) this.tutorialStepRestartTarget.classList.remove("hidden")
    if (this.hasTutorialStepCompletedTarget) this.tutorialStepCompletedTarget.classList.add("hidden")
  }

  render() {
    if (!this.ensureInitialized()) return
    this.updateTutorialPanel()
    if (this.state.completed) {
      this.showGridAside()
      this.showRestrictedUi()
      this.showAchievementsUi()
      this.showSidebarResources()
      this.showSidebarStats()
      this.showSidebarProfileLink()
      this.showSidebarDrawer()
      if (this.hasSidebarToggleTarget) this.showElement(this.sidebarToggleTarget)
      this.showUpgradesTabs()
      return this.clearAllTooltips()
    }

    if (this.isOnDigPage()) {
      this.applyGridAsideVisibility()
    } else {
      this.showGridAside()
    }

    this.applyRestrictedUiVisibility()
    this.applyAchievementsVisibility()
    this.applySidebarResourcesVisibility()
    this.applySidebarStatsVisibility()
    this.applySidebarRecursiveCoinsVisibility()
    this.applySidebarProfileVisibility()
    this.applySidebarDrawerVisibility()
    this.applyUpgradesTabsVisibility()

    if (this.state.step === STEP_ACHIEVEMENTS_NAV && this.isOnAchievementsPage()) {
      this.advanceToStep(STEP_ACHIEVEMENTS_UNCLAIMED, { render: false })
    }

    if (this.state.step === STEP_ACHIEVEMENTS_UNCLAIMED && this.isOnAchievementsPage() && !this.hasUnclaimedAchievements()) {
      this.advanceToStep(STEP_RESOURCES, { render: false })
    }

    if (this.state.step === STEP_RESOURCES && this.isOnUpgradesPage()) {
      this.advanceToStep(STEP_UPGRADES, { render: false })
    }

    this.clearAllTooltips()

    if (this.state.step === STEP_DIG_START || this.state.step === STEP_DIG_WAIT_REWARD) {
      if (!this.isOnDigPage()) return
      if (!this.hasGridCardTarget) return
    }

    if (this.state.step === STEP_DIG_START && this.shouldShowStep1()) {
      this.applyGridTooltip("dig_start")
      return
    }

    if (this.state.step === STEP_DIG_WAIT_REWARD) {
      this.applyGridTooltip("dig_wait_reward")
      return
    }

    if (this.state.step === STEP_ACHIEVEMENTS_NAV) {
      this.applyAchievementsTooltip()
      return
    }

    if (this.state.step === STEP_ACHIEVEMENTS_UNCLAIMED) {
      if (!this.isOnAchievementsPage()) {
        this.applyAchievementsTooltip()
        return
      }

      if (!this.hasUnclaimedAchievements()) return

      this.applyUnclaimedAchievementsTooltip()
      return
    }

    if (this.state.step === STEP_RESOURCES) {
      this.applyResourcesTooltip()
      return
    }

    if (this.state.step === STEP_UPGRADES) {
      if (!this.isOnUpgradesPage()) {
        this.applyResourcesTooltip()
        return
      }
      this.syncCanUpgradeFromDom()

      if (this.state.upgradesInteracted && this.state.canUpgrade === false) {
        this.advanceToStep(STEP_POST_UPGRADES_PROCESS_NAV, { render: true })
        return
      }

      this.applyStatsTooltip()
      if (this.hasUpgradesDeepcoinPanelTarget) this.applyUpgradesTooltip()
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_PROCESS_NAV) {
      if (this.isOnDigPage()) {
        this.advanceToStep(STEP_POST_UPGRADES_DIG_1, { render: true })
        return
      }
      this.applyProcessNavTooltip()
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_DIG_1) {
      if (!this.isOnDigPage()) {
        this.applyProcessNavTooltip()
        return
      }
      this.applyGridTooltip("dig_more")
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_BADGE) {
      if (!this.isOnDigPage()) {
        this.applyProcessNavTooltip()
        return
      }
      this.applyTilesDefeatedTooltip()
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_DIG_2) {
      if (!this.isOnDigPage()) {
        this.applyProcessNavTooltip()
        return
      }
      this.applyGridTooltip("dig_more")
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_QUEUE_MSG) {
      if (!this.isOnDigPage()) {
        this.applyProcessNavTooltip()
        return
      }
      this.applyGridTooltip("unlock_queue_after_25")
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_DIG_3) {
      if (!this.isOnDigPage()) {
        this.applyProcessNavTooltip()
        return
      }
      this.applyGridTooltip("dig_more")
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_OFFLINE_MSG) {
      if (!this.isOnDigPage()) {
        this.applyProcessNavTooltip()
        return
      }
      this.applyGridTooltip("offline_processing")
      return
    }

    if (this.state.step === STEP_PROFILE_LINK) {
      this.applyProfileLinkTooltip()
      return
    }

    if (this.state.step === STEP_PROFILE) {
      if (!this.isOnMyProfilePage()) {
        this.applyProfileLinkTooltip()
        return
      }

      this.applyOperatorProfileTooltip()
      this.applyGoToSettingsTooltip()
      return
    }

    if (this.state.step === STEP_SETTINGS) {
      if (!this.isOnMyProfilePage()) {
        this.applyProfileLinkTooltip()
        return
      }

      if (!this.isWorkerTabActive("settings")) {
        this.applyOperatorProfileTooltip()
        this.applyGoToSettingsTooltip()
        return
      }

      this.applySettingsIntroTooltip()
      this.applyGoToIdentityTooltip()
      return
    }

    if (this.state.step === STEP_IDENTITY) {
      if (this.state.identityUpdateSubmitted && this.hasFlashMessage("Settings updated!")) {
        this.advanceToStep(STEP_ONLINE_OPERATORS, { render: true })
        return
      }

      if (!this.isOnMyProfilePage()) {
        this.applyProfileLinkTooltip()
        return
      }

      if (!this.isWorkerTabActive("identity")) {
        this.applyGoToIdentityTooltip()
        return
      }

      this.applyUpdateIdentityTooltip()
      return
    }

    if (this.state.step === STEP_ONLINE_OPERATORS) {
      if (this.isOnOnlineOperatorsPage()) {
        this.advanceToStep(STEP_COMM_INTRO, { render: true })
        return
      }
      this.applyOnlineOperatorsTooltip()
      return
    }

    if (this.state.step === STEP_COMM_INTRO) {
      if (!this.isOnOnlineOperatorsPage()) {
        this.applyOnlineOperatorsTooltip()
        return
      }

      this.applyCommTooltip()
      return
    }

    if (this.state.step === STEP_PERFORMANCE_NAV) {
      if (this.isOnPerformancePage()) {
        this.advanceToStep(STEP_PERFORMANCE_PAGE, { render: true })
        return
      }

      this.applyPerformanceNavTooltip()
      return
    }

    if (this.state.step === STEP_PERFORMANCE_PAGE) {
      this.applyPerformanceDoneTooltip()
      return
    }
  }

  shouldShowStep1() {
    const timesDugDelta = this.deltaFromRestart(this.currentTimesDug(), this.state.restartTimesDug)
    return Number.isFinite(timesDugDelta) ? timesDugDelta <= 0 : false
  }

  isOnDigPage() {
    try {
      return window.location.pathname === "/dig"
    } catch {
      return false
    }
  }

  isOnAchievementsPage() {
    try {
      return window.location.pathname === "/achievements"
    } catch {
      return false
    }
  }

  isOnUpgradesPage() {
    try {
      return window.location.pathname === "/upgrades"
    } catch {
      return false
    }
  }

  isOnOnlineOperatorsPage() {
    try {
      return window.location.pathname === "/workers/online"
    } catch {
      return false
    }
  }

  isOnPerformancePage() {
    try {
      return window.location.pathname === "/legends"
    } catch {
      return false
    }
  }

  tooltipDef(key) {
    const def = TOOLTIP_DEFS[key]
    if (!def) {
      console.warn("[tutorial] Unknown tooltip key:", key)
      return null
    }
    return def
  }

  applyTooltipKeyTo(element, key, overrides = {}) {
    const def = this.tooltipDef(key)
    if (!def) return
    this.applyTooltipTo(element, { ...def, ...overrides })
  }

  applyGridTooltip(key, overrides = {}) {
    if (!this.hasGridCardTarget) return
    this.applyTooltipKeyTo(this.gridCardTarget, key, overrides)
  }

  applyFloatingTooltipKeyNear(target, key, overrides = {}) {
    const def = this.tooltipDef(key)
    if (!def) return
    this.applyFloatingTooltipNear(target, { ...def, ...overrides })
  }

  applyTooltipTo(element, { text, variant, position = "top" }) {
    if (!element) return

    element.dataset.tip = text
    element.classList.add("tooltip", "tooltip-open")
    element.classList.toggle("tooltip-top", position === "top")
    element.classList.toggle("tooltip-right", position === "right")
    element.classList.toggle("tooltip-left", position === "left")
    element.classList.toggle("tooltip-bottom", position === "bottom")

    element.classList.toggle("tooltip-warning", variant === "warning")
    element.classList.toggle("tooltip-success", variant === "success")
    element.classList.toggle("tooltip-info", variant === "info")
  }

  clearAllTooltips() {
    if (this.hasGridCardTarget) this.clearTooltipFrom(this.gridCardTarget)
    if (this.hasSidebarToggleTarget) this.clearTooltipFrom(this.sidebarToggleTarget)
    if (this.hasSidebarProcessLinkTarget) this.clearTooltipFrom(this.sidebarProcessLinkTarget)
    if (this.hasOnlineOperatorsLinkTarget) this.clearTooltipFrom(this.onlineOperatorsLinkTarget)
    if (this.hasPerformanceLinkTarget) this.clearTooltipFrom(this.performanceLinkTarget)
    if (this.hasUnclaimedAchievementsTarget) this.clearTooltipFrom(this.unclaimedAchievementsTarget)
    if (this.hasSidebarDeepcoinsLinkTarget) this.clearTooltipFrom(this.sidebarDeepcoinsLinkTarget)
    if (this.hasUpgradesDeepcoinPanelTarget) this.clearTooltipFrom(this.upgradesDeepcoinPanelTarget)
    if (this.hasSidebarStatsDividerTarget) this.clearTooltipFrom(this.sidebarStatsDividerTarget)
    if (this.hasSidebarStatsContentTarget) this.clearTooltipFrom(this.sidebarStatsContentTarget)
    if (this.hasTilesDefeatedBadgeTarget) this.clearTooltipFrom(this.tilesDefeatedBadgeTarget)
    if (this.statsTooltipAnchor) this.clearTooltipFrom(this.statsTooltipAnchor)
    this.statsTooltipAnchor = null
    this.restoreTooltipOverflow()
    this.removeFloatingTooltip()
    if (this.commTooltipAnchor) this.clearTooltipFrom(this.commTooltipAnchor)
    this.commTooltipAnchor = null
    if (this.hasSidebarNameLinkTarget) this.clearTooltipFrom(this.sidebarNameLinkTarget)
    if (this.hasWorkerProfileSectionTarget) this.clearTooltipFrom(this.workerProfileSectionTarget)
    if (this.hasProfileSettingsTabTarget) this.clearTooltipFrom(this.profileSettingsTabTarget)
    if (this.hasProfileIdentityTabTarget) this.clearTooltipFrom(this.profileIdentityTabTarget)
    if (this.hasProfileSettingsPanelTarget) this.clearTooltipFrom(this.profileSettingsPanelTarget)
    if (this.hasProfileSettingsTopFieldsetTarget) this.clearTooltipFrom(this.profileSettingsTopFieldsetTarget)
    if (this.hasProfileOperatorProfileFieldsetTarget) this.clearTooltipFrom(this.profileOperatorProfileFieldsetTarget)
    if (this.hasProfileIdentityFieldsetTarget) this.clearTooltipFrom(this.profileIdentityFieldsetTarget)
    if (this.hasProfileUpdateIdentityButtonTarget) this.clearTooltipFrom(this.profileUpdateIdentityButtonTarget)
    if (this.hasPerformancePageHeaderTarget) this.clearTooltipFrom(this.performancePageHeaderTarget)

    if (this.hasAchievementsLinkTarget) {
      this.achievementsLinkTargets.forEach((el) => this.clearTooltipFrom(el))
    }
  }

  clearTooltipFrom(element) {
    if (!element) return

    delete element.dataset.tip

    element.classList.remove(
      "tooltip",
      "tooltip-open",
      "tooltip-top",
      "tooltip-right",
      "tooltip-left",
      "tooltip-bottom",
      "tooltip-warning",
      "tooltip-success",
      "tooltip-info"
    )
  }

  applyAchievementsTooltip() {
    const def = this.tooltipDef("achievements_nav")
    if (!def) return
    const { text, variant, position } = def
    const visibleLink = (this.hasAchievementsLinkTarget ? this.achievementsLinkTargets : []).find((el) =>
      this.isSidebarTargetVisible(el)
    )

    if (visibleLink) {
      this.applyTooltipTo(visibleLink, { text, variant, position })
      return
    }

    if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
      this.applyTooltipTo(this.sidebarToggleTarget, { text, variant, position: "right" })
    }
  }

  applyUnclaimedAchievementsTooltip() {
    if (!this.hasUnclaimedAchievementsTarget) return
    this.applyTooltipKeyTo(this.unclaimedAchievementsTarget, "achievements_unclaimed")
  }

  applyResourcesTooltip() {
    if (this.hasSidebarDeepcoinsLinkTarget && this.isSidebarTargetVisible(this.sidebarDeepcoinsLinkTarget)) {
      this.applyTooltipKeyTo(this.sidebarDeepcoinsLinkTarget, "resources_deepcoins")
      return
    }

    if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
      this.applyTooltipKeyTo(this.sidebarToggleTarget, "resources_deepcoins", { position: "right" })
    }
  }

  applyProcessNavTooltip() {
    if (this.hasSidebarProcessLinkTarget && this.isSidebarTargetVisible(this.sidebarProcessLinkTarget)) {
      this.applyTooltipKeyTo(this.sidebarProcessLinkTarget, "process_nav_after_upgrades")
      return
    }

    if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
      this.applyTooltipKeyTo(this.sidebarToggleTarget, "process_nav_after_upgrades", { position: "right" })
    }
  }

  applyUpgradesTooltip() {
    this.applyTooltipKeyTo(this.upgradesDeepcoinPanelTarget, "upgrades_choose")
  }

  applyProfileLinkTooltip() {
    const def = this.tooltipDef("profile_link")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasSidebarNameLinkTarget && this.isSidebarTargetVisible(this.sidebarNameLinkTarget)) {
      this.ensureTooltipOverflow(this.sidebarNameLinkTarget)
      this.applyTooltipTo(this.sidebarNameLinkTarget, { text, variant, position })
      return
    }

    if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
      this.applyTooltipTo(this.sidebarToggleTarget, { text, variant, position: "right" })
    }
  }

  applyOperatorProfileTooltip() {
    const def = this.tooltipDef("profile_intro")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasProfileOperatorProfileFieldsetTarget && this.isElementVisible(this.profileOperatorProfileFieldsetTarget)) {
      this.ensureTooltipOverflow(this.profileOperatorProfileFieldsetTarget)
      this.applyTooltipTo(this.profileOperatorProfileFieldsetTarget, { text, variant, position })
      return
    }

    if (!this.hasWorkerProfileSectionTarget) return
    this.ensureTooltipOverflow(this.workerProfileSectionTarget)
    this.applyTooltipTo(this.workerProfileSectionTarget, { text, variant, position })
  }

  applyGoToSettingsTooltip() {
    if (!this.hasProfileSettingsTabTarget) return
    this.applyFloatingTooltipKeyNear(this.profileSettingsTabTarget, "profile_to_settings")
  }

  applySettingsIntroTooltip() {
    const def = this.tooltipDef("settings_intro")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasProfileSettingsTopFieldsetTarget && this.isElementVisible(this.profileSettingsTopFieldsetTarget)) {
      this.ensureTooltipOverflow(this.profileSettingsTopFieldsetTarget)
      this.applyTooltipTo(this.profileSettingsTopFieldsetTarget, { text, variant, position })
      return
    }

    if (!this.hasProfileSettingsPanelTarget) return
    this.ensureTooltipOverflow(this.profileSettingsPanelTarget)
    this.applyTooltipTo(this.profileSettingsPanelTarget, { text, variant, position })
  }

  applyGoToIdentityTooltip() {
    if (!this.hasProfileIdentityTabTarget) return
    this.applyFloatingTooltipKeyNear(this.profileIdentityTabTarget, "settings_to_identity")
  }

  applyFloatingTooltipNear(target, { text, variant, position = "top" }) {
    if (!(target instanceof Element)) return
    if (!this.isElementVisible(target)) return

    const rect = target.getBoundingClientRect()
    const scrollX = window.scrollX || window.pageXOffset || 0
    const scrollY = window.scrollY || window.pageYOffset || 0

    let left = rect.left + rect.width / 2 + scrollX
    let top = rect.top + rect.height / 2 + scrollY

    if (position === "top") top = rect.top + scrollY
    if (position === "bottom") top = rect.bottom + scrollY
    if (position === "right") left = rect.right + scrollX

    const el = this.getOrCreateFloatingTooltip()
    el.dataset.tip = text

    el.classList.toggle("tooltip-warning", variant === "warning")
    el.classList.toggle("tooltip-success", variant === "success")
    el.classList.toggle("tooltip-info", variant === "info")
    el.classList.toggle("tooltip-top", position === "top")
    el.classList.toggle("tooltip-right", position === "right")
    el.classList.toggle("tooltip-bottom", position === "bottom")

    el.style.left = `${Math.round(left)}px`
    el.style.top = `${Math.round(top)}px`
  }

  getOrCreateFloatingTooltip() {
    if (this.floatingTooltipEl && document.body.contains(this.floatingTooltipEl)) return this.floatingTooltipEl

    const el = document.createElement("span")
    el.className = "tooltip tooltip-open"
    el.style.position = "absolute"
    el.style.width = "1px"
    el.style.height = "1px"
    el.style.pointerEvents = "none"
    el.style.zIndex = "9999"
    document.body.appendChild(el)
    this.floatingTooltipEl = el
    return el
  }

  removeFloatingTooltip() {
    if (!this.floatingTooltipEl) return
    try {
      this.floatingTooltipEl.remove()
    } catch {
      // ignore
    }
    this.floatingTooltipEl = null
  }

  applyUpdateIdentityTooltip() {
    const def = this.tooltipDef("identity_update")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasProfileIdentityFieldsetTarget && this.isElementVisible(this.profileIdentityFieldsetTarget)) {
      this.ensureTooltipOverflow(this.profileIdentityFieldsetTarget)
      this.applyTooltipTo(this.profileIdentityFieldsetTarget, { text, variant, position })
      return
    }

    if (!this.hasProfileUpdateIdentityButtonTarget) return
    this.ensureTooltipOverflow(this.profileUpdateIdentityButtonTarget)
    this.applyTooltipTo(this.profileUpdateIdentityButtonTarget, { text, variant, position })
  }

  applyOnlineOperatorsTooltip() {
    const def = this.tooltipDef("online_operators")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasSidebarOnlineOperatorsTarget) this.showElement(this.sidebarOnlineOperatorsTarget)

    if (this.hasOnlineOperatorsLinkTarget && this.isSidebarTargetVisible(this.onlineOperatorsLinkTarget)) {
      this.applyTooltipTo(this.onlineOperatorsLinkTarget, { text, variant, position })
      return
    }

    if (this.hasSidebarOnlineOperatorsTarget && this.isSidebarTargetVisible(this.sidebarOnlineOperatorsTarget)) {
      this.applyTooltipTo(this.sidebarOnlineOperatorsTarget, { text, variant, position })
      return
    }

    if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
      this.applyTooltipTo(this.sidebarToggleTarget, { text, variant, position: "right" })
    }
  }

  applyCommTooltip() {
    if (this.hasCommAreaTarget) this.showElement(this.commAreaTarget)
    if (!this.hasCommAreaTarget) return

    const commButton = this.commAreaTarget.querySelector('button[aria-controls="utility-panel-comm"]')
    const anchor = commButton instanceof Element ? commButton : this.commAreaTarget

    this.commTooltipAnchor = anchor
    this.applyTooltipKeyTo(anchor, "comm_intro")
  }

  applyPerformanceNavTooltip() {
    const def = this.tooltipDef("performance_nav")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasPerformanceLinkTarget && this.isSidebarTargetVisible(this.performanceLinkTarget)) {
      this.applyTooltipTo(this.performanceLinkTarget, { text, variant, position })
      return
    }

    if (this.hasSidebarPerformanceTarget && this.isSidebarTargetVisible(this.sidebarPerformanceTarget)) {
      this.applyTooltipTo(this.sidebarPerformanceTarget, { text, variant, position })
      return
    }

    if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
      this.applyTooltipTo(this.sidebarToggleTarget, { text, variant, position: "right" })
    }
  }

  applyPerformanceDoneTooltip() {
    const def = this.tooltipDef("performance_done")
    if (!def) return
    const { text, variant, position } = def

    if (this.hasPerformancePageHeaderTarget && this.isElementVisible(this.performancePageHeaderTarget)) {
      this.ensureTooltipOverflow(this.performancePageHeaderTarget)
      this.applyTooltipTo(this.performancePageHeaderTarget, { text, variant, position })
      return
    }

    const mainPanel = document.getElementById("main-panel")
    if (mainPanel instanceof Element && this.isElementVisible(mainPanel)) {
      this.ensureTooltipOverflow(mainPanel)
      this.applyTooltipTo(mainPanel, { text, variant, position })
    }
  }

  applyTilesDefeatedTooltip() {
    if (this.hasGridAsideTarget) this.showGridAside()

    if (this.hasTilesDefeatedBadgeTarget && this.isElementVisible(this.tilesDefeatedBadgeTarget)) {
      this.applyTooltipKeyTo(this.tilesDefeatedBadgeTarget, "tiles_defeated_badge")
      return
    }

    if (this.hasGridAsideTarget && this.isElementVisible(this.gridAsideTarget)) {
      this.applyTooltipKeyTo(this.gridAsideTarget, "tiles_defeated_badge")
      return
    }

    this.applyGridTooltip("tiles_defeated_badge")
  }

  applyStatsTooltip() {
    const def = this.tooltipDef("upgrades_stats")
    if (!def) return
    const { text, variant } = def
    const isMobileLayout = this.isSidebarToggleDisplayed()
    const drawerOpen = this.isSidebarDrawerOpen()

    const statsDividerButton = this.hasSidebarStatsDividerTarget
      ? this.sidebarStatsDividerTarget.querySelector('button[data-section="stats"]')
      : null

    if (isMobileLayout && !drawerOpen) {
      if (this.hasSidebarToggleTarget && this.isElementVisible(this.sidebarToggleTarget)) {
        this.applyTooltipTo(this.sidebarToggleTarget, { text, variant, position: "right" })
      }
      return
    }

    if (this.hasSidebarStatsContentTarget && this.isSidebarTargetVisible(this.sidebarStatsContentTarget)) {
      this.statsTooltipAnchor = this.sidebarStatsContentTarget
      this.ensureTooltipOverflow(this.sidebarStatsContentTarget)
      this.applyTooltipTo(this.sidebarStatsContentTarget, { text, variant, position: "bottom" })
      return
    }

    const statsBox = this.getSidebarStatsBox()
    if (statsBox && this.isSidebarTargetVisible(statsBox)) {
      this.statsTooltipAnchor = statsBox
      this.ensureTooltipOverflow(statsBox)
      this.applyTooltipTo(statsBox, { text, variant, position: "bottom" })
      return
    }

    if (statsDividerButton instanceof Element && this.isSidebarTargetVisible(statsDividerButton)) {
      this.statsTooltipAnchor = statsDividerButton
      this.applyTooltipTo(statsDividerButton, { text, variant, position: "bottom" })
      return
    }

    if (this.hasSidebarStatsDividerTarget && this.isSidebarTargetVisible(this.sidebarStatsDividerTarget)) {
      this.statsTooltipAnchor = this.sidebarStatsDividerTarget
      this.ensureTooltipOverflow(this.sidebarStatsDividerTarget)
      this.applyTooltipTo(this.sidebarStatsDividerTarget, { text, variant, position: "bottom" })
    }
  }

  getSidebarStatsBox() {
    if (!this.hasSidebarStatsContentTarget) return null
    const el = this.sidebarStatsContentTarget.querySelector('[data-component="damage-stats"]')
    return el instanceof Element ? el : null
  }

  ensureTooltipOverflow(element) {
    if (!element) return
    const container = element.closest(".truncate") || element.closest(".card") || element.closest(".tabs")
    if (!(container instanceof Element)) return

    const style = window.getComputedStyle(container)
    if (style.overflow === "visible" && style.overflowX === "visible" && style.overflowY === "visible") return

    if (typeof container.dataset.tutorialOriginalOverflow === "undefined") {
      container.dataset.tutorialOriginalOverflow = container.style.overflow || ""
      container.dataset.tutorialOriginalOverflowX = container.style.overflowX || ""
      container.dataset.tutorialOriginalOverflowY = container.style.overflowY || ""
    }

    container.style.overflow = "visible"
    container.style.overflowX = "visible"
    container.style.overflowY = "visible"
    this.tooltipOverflowAdjusted = true
  }

  restoreTooltipOverflow() {
    if (!this.tooltipOverflowAdjusted) return
    this.tooltipOverflowAdjusted = false

    const elements = Array.from(document.querySelectorAll("[data-tutorial-original-overflow]"))
    elements.forEach((el) => {
      if (!(el instanceof Element)) return
      el.style.overflow = el.dataset.tutorialOriginalOverflow || ""
      el.style.overflowX = el.dataset.tutorialOriginalOverflowX || ""
      el.style.overflowY = el.dataset.tutorialOriginalOverflowY || ""
      delete el.dataset.tutorialOriginalOverflow
      delete el.dataset.tutorialOriginalOverflowX
      delete el.dataset.tutorialOriginalOverflowY
    })
  }

  applyGridAsideVisibility() {
    if (!this.hasGridAsideTarget) return
    if (this.state.gridAsideUnlocked) return this.showGridAside()
    this.hideGridAside()
  }

  hideGridAside(element = null) {
    const aside = element || (this.hasGridAsideTarget ? this.gridAsideTarget : null)
    this.hideElement(aside)
  }

  showGridAside(element = null) {
    const aside = element || (this.hasGridAsideTarget ? this.gridAsideTarget : null)
    this.showElement(aside)
  }

  hasUnclaimedAchievements() {
    return this.hasUnclaimedAchievementsTarget
  }

  isElementVisible(element) {
    if (!element) return false
    if (element.offsetParent === null) return false
    const style = window.getComputedStyle(element)
    if (style.visibility === "hidden" || style.display === "none") return false
    const rect = element.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return false

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    return rect.bottom >= 0 && rect.right >= 0 && rect.top <= viewportHeight && rect.left <= viewportWidth
  }

  currentTimesDug() {
    return this.hasTimesDugValue && Number.isFinite(this.timesDugValue) ? this.timesDugValue : null
  }

  currentDeepCoins() {
    return this.hasDeepCoinsValue && Number.isFinite(this.deepCoinsValue) ? this.deepCoinsValue : null
  }

  deltaFromRestart(currentValue, baseline) {
    if (!Number.isFinite(currentValue)) return null
    if (!Number.isFinite(baseline)) return currentValue
    return currentValue - baseline
  }

  isElementRendered(element) {
    if (!element) return false
    if (!element.isConnected) return false
    const style = window.getComputedStyle(element)
    if (style.visibility === "hidden" || style.display === "none") return false
    const rect = element.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  getSidebarToggleInput() {
    const input = document.getElementById("deepco-sidebar-toggle")
    return input instanceof HTMLInputElement ? input : null
  }

  isSidebarToggleDisplayed() {
    if (!this.hasSidebarToggleTarget) return false
    const style = window.getComputedStyle(this.sidebarToggleTarget)
    return style.display !== "none" && style.visibility !== "hidden"
  }

  isSidebarDrawerOpen() {
    if (!this.isSidebarToggleDisplayed()) return true
    const input = this.getSidebarToggleInput()
    if (input) return input.checked
    if (!this.hasSidebarDrawerTarget) return false
    const rect = this.sidebarDrawerTarget.getBoundingClientRect()
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    return rect.right > 0 && rect.left < viewportWidth
  }

  isSidebarTargetVisible(element) {
    if (!element) return false
    if (this.isSidebarToggleDisplayed()) {
      if (!this.isSidebarDrawerOpen()) return false
      return this.isElementRendered(element)
    }
    return this.isElementVisible(element)
  }

  loadState() {
    const fallback = this.initialState()
    try {
      const raw = localStorage.getItem(this.storageKey)
      if (!raw) return fallback
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== "object") return fallback
      if (!Number.isFinite(parsed.step) || parsed.step < 1 || parsed.step > COMPLETION_STEP) return fallback

      if (parsed.schemaVersion !== fallback.schemaVersion) {
        const migrated = { ...fallback, ...parsed, schemaVersion: fallback.schemaVersion }
        if (migrated.completed) {
          migrated.step = COMPLETION_STEP
          return migrated
        }
        // Major step-order change: restart post-upgrades segment for any in-progress tutorial past upgrades.
        if (Number.isFinite(migrated.step) && migrated.step >= STEP_POST_UPGRADES_PROCESS_NAV) {
          migrated.step = STEP_POST_UPGRADES_PROCESS_NAV
        }
        migrated.identityUpdateSubmitted = false
        migrated.gridAsideUnlocked = false
        return migrated
      }

      return { ...fallback, ...parsed }
    } catch {
      return fallback
    }
  }

  initialState() {
    return {
      schemaVersion: 2,
      step: STEP_DIG_START,
      completed: false,
      updatedAt: Date.now(),
      upgradesInteracted: false,
      canUpgrade: null,
      identityUpdateSubmitted: false,
      gridAsideUnlocked: false,
      restartTimesDug: null,
      restartDeepCoins: null,
      awaitedDigTileId: null,
      tilesDefeated: null,
      awaitedTilesDefeated: null
    }
  }

  saveState() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.state))
    } catch {
      // Ignore storage access issues (e.g., Safari private mode)
    }
  }

  ensureInitialized() {
    if (this.initialized) return true
    if (!this.tutorialModeValue) return false
    if (!this.hasWorkerIdValue || !Number.isFinite(this.workerIdValue)) return false

    this.storageKey = `deepco:tutorial:v1:worker:${this.workerIdValue}`
    this.state = this.loadState()

    if (this.state.completed && this.hasTutorialModePathValue && this.tutorialModePathValue) {
      this.persistTutorialMode(false)
    }

    this.initialized = true
    return true
  }

  applyRestrictedUiVisibility() {
    if (this.state.step >= COMPLETION_STEP) {
      this.showRestrictedUi()
      return
    }

    if (this.hasCommAreaTarget) {
      if (this.state.step >= STEP_COMM_INTRO) {
        this.showElement(this.commAreaTarget)
      } else {
        this.hideElement(this.commAreaTarget)
      }
    }
    if (this.hasSidebarRecursionTarget) this.hideElement(this.sidebarRecursionTarget)
    if (this.hasSidebarPerformanceTarget) {
      if (this.state.step >= STEP_PERFORMANCE_NAV) {
        this.showElement(this.sidebarPerformanceTarget)
      } else {
        this.hideElement(this.sidebarPerformanceTarget)
      }
    }
    if (this.hasSidebarShopTarget) this.hideElement(this.sidebarShopTarget)
    if (this.hasSidebarOnlineOperatorsTarget) {
      if (this.state.step >= STEP_ONLINE_OPERATORS) {
        this.showElement(this.sidebarOnlineOperatorsTarget)
      } else {
        this.hideElement(this.sidebarOnlineOperatorsTarget)
      }
    }
    if (this.hasSidebarMsgTarget) this.hideElement(this.sidebarMsgTarget)
  }

  showRestrictedUi() {
    if (this.hasCommAreaTarget) this.showElement(this.commAreaTarget)
    if (this.hasSidebarRecursionTarget) this.showElement(this.sidebarRecursionTarget)
    if (this.hasSidebarPerformanceTarget) this.showElement(this.sidebarPerformanceTarget)
    if (this.hasSidebarShopTarget) this.showElement(this.sidebarShopTarget)
    if (this.hasSidebarOnlineOperatorsTarget) this.showElement(this.sidebarOnlineOperatorsTarget)
    if (this.hasSidebarMsgTarget) this.showElement(this.sidebarMsgTarget)
  }

  tileIdFromWrapper(wrapper) {
    if (!(wrapper instanceof Element)) return null
    const raw = wrapper.getAttribute("data-tile-tile-id-value")
    const tileId = Number.parseInt(String(raw || ""), 10)
    if (!Number.isFinite(tileId) || tileId <= 0) return null
    return tileId
  }

  didAwaitedDigComplete(payload) {
    const tileId = Number(payload?.tile_id)
    const awaitedTileId = this.state.awaitedDigTileId

    if (Number.isFinite(awaitedTileId)) {
      if (!Number.isFinite(tileId)) return false
      if (tileId !== awaitedTileId) return false
    }

    const tileDug = Boolean(payload?.tile_dug)
    const tileHealth = Number(payload?.tile_health)
    if (!tileDug && !(Number.isFinite(tileHealth) && tileHealth <= 0)) return false

    if (!Number.isFinite(awaitedTileId)) {
      const damages = Array.isArray(payload.damages) ? payload.damages : []
      const didWeDamage = damages.some((d) => Number(d?.worker_id) === this.workerIdValue)
      if (!didWeDamage) return false
    }

    return true
  }

  syncTilesDefeatedFromDom({ save = true } = {}) {
    const domValue = this.readTilesDefeatedFromDom()
    if (!Number.isFinite(domValue) || domValue < 0) return
    if (this.state.tilesDefeated === domValue) return
    this.state.tilesDefeated = domValue
    this.state.updatedAt = Date.now()
    if (save) this.saveState()
  }

  readTilesDefeatedFromDom() {
    const target =
      (this.hasTilesDefeatedBadgeTarget ? this.tilesDefeatedBadgeTarget : null) ||
      document.getElementById("tiles-defeated-badge") ||
      document.querySelector(".tile-progression")

    if (!(target instanceof Element)) return null
    const raw = String(target.textContent || "")
      .replace(/,/g, "")
      .trim()

    const match = raw.match(/(\d+)\s*\//)
    if (!match) return null
    const value = Number.parseInt(match[1], 10)
    return Number.isFinite(value) ? value : null
  }

  isTileDefeatWaitStep(step) {
    return [STEP_POST_UPGRADES_BADGE, STEP_POST_UPGRADES_QUEUE_MSG, STEP_POST_UPGRADES_OFFLINE_MSG].includes(step)
  }

  maybeCompleteTileDefeatWaitStep({ render = true } = {}) {
    if (!this.isTileDefeatWaitStep(this.state.step)) return

    const current = Number.isFinite(this.state.tilesDefeated) ? this.state.tilesDefeated : this.readTilesDefeatedFromDom()
    const baseline = this.state.awaitedTilesDefeated

    const didDefeatATile =
      Number.isFinite(current) && Number.isFinite(baseline) ? current > baseline : false

    const didAwaitedTileBecomeDug = this.awaitedTileIsDugInDom(this.state.awaitedDigTileId)

    if (!didDefeatATile && !didAwaitedTileBecomeDug) return

    if (this.state.step === STEP_POST_UPGRADES_BADGE) {
      this.advanceToStep(STEP_POST_UPGRADES_DIG_2, { render })
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_QUEUE_MSG) {
      this.advanceToStep(STEP_POST_UPGRADES_DIG_3, { render })
      return
    }

    if (this.state.step === STEP_POST_UPGRADES_OFFLINE_MSG) {
      this.advanceToStep(STEP_PROFILE_LINK, { render })
    }
  }

  awaitedTileIsDugInDom(tileId) {
    if (!Number.isFinite(tileId) || tileId <= 0) return false
    const tile = document.querySelector(`#tile_inner_${tileId} .tile`)
    if (!(tile instanceof Element)) return false
    return !tile.classList.contains("undug")
  }

  applyAchievementsVisibility() {
    if (this.state.step >= 3) {
      this.showAchievementsUi()
      return
    }

    if (this.hasSidebarAchievementsTarget) this.hideElement(this.sidebarAchievementsTarget)
  }

  showAchievementsUi() {
    if (this.hasSidebarAchievementsTarget) this.showElement(this.sidebarAchievementsTarget)
  }

  applySidebarResourcesVisibility() {
    if (this.state.step >= 5) {
      this.showSidebarResources()
      return
    }

    if (this.hasSidebarResourcesDividerTarget) this.hideElement(this.sidebarResourcesDividerTarget)
    if (this.hasSidebarResourcesContentTarget) this.hideElement(this.sidebarResourcesContentTarget)
  }

  showSidebarResources() {
    if (this.hasSidebarResourcesDividerTarget) this.showElement(this.sidebarResourcesDividerTarget)
    if (this.hasSidebarResourcesContentTarget) this.showElement(this.sidebarResourcesContentTarget)
  }

  applySidebarRecursiveCoinsVisibility() {
    if (!this.hasSidebarRecursiveCoinsLinkTarget) return

    if (this.state.step >= COMPLETION_STEP) {
      this.showElement(this.sidebarRecursiveCoinsLinkTarget)
      return
    }

    // Only relevant once Resources are visible; otherwise it's hidden anyway.
    this.hideElement(this.sidebarRecursiveCoinsLinkTarget)
  }

  applySidebarStatsVisibility() {
    if (this.state.step >= 6) {
      this.showSidebarStats()
      return
    }

    if (this.hasSidebarStatsDividerTarget) this.hideElement(this.sidebarStatsDividerTarget)
    if (this.hasSidebarStatsContentTarget) this.hideElement(this.sidebarStatsContentTarget)
  }

  showSidebarStats() {
    if (this.hasSidebarStatsDividerTarget) this.showElement(this.sidebarStatsDividerTarget)
    if (this.hasSidebarStatsContentTarget) this.showElement(this.sidebarStatsContentTarget)
  }

  applySidebarProfileVisibility() {
    if (this.state.step >= STEP_PROFILE_LINK) {
      this.showSidebarProfileLink()
      return
    }

    if (this.hasSidebarNameTextTarget) this.showElement(this.sidebarNameTextTarget)
    if (this.hasSidebarNameLinkTarget) this.hideElement(this.sidebarNameLinkTarget)
  }

  showSidebarProfileLink() {
    if (this.hasSidebarNameTextTarget) this.hideElement(this.sidebarNameTextTarget)
    if (this.hasSidebarNameLinkTarget) this.showElement(this.sidebarNameLinkTarget)
  }

  applySidebarDrawerVisibility() {
    if (this.state.step >= 3) {
      this.showSidebarDrawer()
      if (this.hasSidebarToggleTarget) this.showElement(this.sidebarToggleTarget)
      return
    }

    if (this.hasSidebarDrawerTarget) this.hideElement(this.sidebarDrawerTarget)
    if (this.hasSidebarToggleTarget) this.hideElement(this.sidebarToggleTarget)
  }

  showSidebarDrawer() {
    if (this.hasSidebarDrawerTarget) this.showElement(this.sidebarDrawerTarget)
  }

  applyUpgradesTabsVisibility() {
    if (!this.isOnUpgradesPage()) return

    if (this.state.step === STEP_UPGRADES) {
      if (this.hasUpgradesRecursiveTabTarget) this.hideElement(this.upgradesRecursiveTabTarget)
      if (this.hasUpgradesSpecializationTabTarget) this.hideElement(this.upgradesSpecializationTabTarget)
      return
    }

    this.showUpgradesTabs()
  }

  showUpgradesTabs() {
    if (this.hasUpgradesRecursiveTabTarget) this.showElement(this.upgradesRecursiveTabTarget)
    if (this.hasUpgradesSpecializationTabTarget) this.showElement(this.upgradesSpecializationTabTarget)
  }

  isOnMyProfilePage() {
    try {
      const match = window.location.pathname.match(/^\/workers\/(\d+)(?:\/|$)/)
      if (!match) return false
      return Number(match[1]) === this.workerIdValue
    } catch {
      return false
    }
  }

  isWorkerTabActive(tabId) {
    const input = document.getElementById(`worker_tab_${tabId}`)
    return input instanceof HTMLInputElement ? input.checked : false
  }

  hasFlashMessage(text) {
    const container = document.getElementById("flash-messages")
    if (!container) return false
    return container.textContent?.includes(text)
  }

  identityFormHasErrors() {
    if (!this.hasProfileIdentityFormTarget) return false
    return this.profileIdentityFormTarget.querySelector(".input-error") instanceof Element
  }

  syncCanUpgradeFromDom({ save = true } = {}) {
    const domValue = this.readCanUpgradeFromDom()
    if (typeof domValue !== "boolean") return
    if (this.state.canUpgrade === domValue) return
    this.state.canUpgrade = domValue
    this.state.updatedAt = Date.now()
    if (save) this.saveState()
  }

  readCanUpgradeFromDom() {
    const indicator = document.querySelector('[data-role="upgrade-indicator"]')
    if (!(indicator instanceof Element)) return null
    return !indicator.classList.contains("hidden")
  }

  hideElement(element) {
    if (!element) return
    if (typeof element.dataset.tutorialOriginalStyle === "undefined") {
      element.dataset.tutorialOriginalStyle = element.getAttribute("style") || ""
    }

    element.style.display = "none"
    element.style.visibility = "hidden"
    element.style.width = "0"
    element.style.maxWidth = "0"
    element.style.height = "0"
    element.style.maxHeight = "0"
    element.style.flex = "0 0 0"
    element.style.overflow = "hidden"
    element.style.margin = "0"
    element.style.padding = "0"
    element.style.pointerEvents = "none"
  }

  showElement(element) {
    if (!element) return
    const originalStyle = element.dataset.tutorialOriginalStyle

    if (typeof originalStyle !== "undefined") {
      if (originalStyle === "") {
        element.removeAttribute("style")
      } else {
        element.setAttribute("style", originalStyle)
      }
      delete element.dataset.tutorialOriginalStyle
      return
    }

    element.style.display = ""
    element.style.visibility = ""
    element.style.width = ""
    element.style.maxWidth = ""
    element.style.height = ""
    element.style.maxHeight = ""
    element.style.flex = ""
    element.style.overflow = ""
    element.style.margin = ""
    element.style.padding = ""
    element.style.pointerEvents = ""
  }
}
